import { cn } from '@/core/lib/utils/cn'

type Variant = 'text' | 'outlined' | 'contained'
type Radius = 'xs' | 'sm' | 'md' | 'lg' | 'full'
type Color =
  | 'cyan-sea'
  | 'mountain-bluebird'
  | 'red'
  | 'flusing-pink'
  | 'white'
  | 'stalacitite'
  | 'gray-morning'
  | 'black-robe'
  | 'creamed-cron'
  | 'secret-spaces'
  | 'arctic-seal'
  | 'cyan-blue'
  | 'medium-gray'

type Skin = 'solid' | 'light' | 'secondary-light' | 'ghost'
const radiusMap: Record<Radius, string> = {
  xs: 'rounded-sm',
  sm: 'rounded-md',
  md: 'rounded-lg',
  lg: 'rounded-xl',
  full: 'rounded-full',
}

type ButtonOwnProps = {
  variant?: Variant
  radius?: Radius
  color?: Color
  loading?: boolean
  fullWidth?: boolean
  className?: string
  children?: React.ReactNode
  startContent?: React.ReactNode
  justifyContent?: 'start' | 'end' | 'center'
  skin?: Skin
}

type PolymorphicProps<C extends React.ElementType, Props = object> = Props &
  Omit<React.ComponentPropsWithoutRef<C>, keyof Props> & {
    component?: C
  }

export type ButtonProps<C extends React.ElementType = 'button'> =
  PolymorphicProps<
    C,
    ButtonOwnProps & {
      disabled?: boolean
    }
  >

export const Button = <C extends React.ElementType = 'button'>({
  component,
  children,
  className,
  fullWidth = false,
  variant = 'contained',
  radius = 'sm',
  color = 'creamed-cron',
  loading = false,
  skin = 'solid',
  disabled,
  startContent = null,
  justifyContent = 'center',
  ...rest
}: ButtonProps<C>) => {
  const Component = component || 'button'

  const isDisabled = disabled || loading
  const baseColor = `var(--color-${color})`

  const variantClasses = cn(
    variant === 'text' && 'bg-transparent border-none',
    variant === 'outlined' && 'bg-transparent border',
    variant === 'contained' && 'text-white',
  )
  const justifyClasses = cn(
    justifyContent === 'center' && 'justify-center',
    justifyContent === 'end' && 'justify-end',
    justifyContent === 'start' && 'justify-start',
  )

  const dynamicStyles: React.CSSProperties = {
    backgroundColor:
      variant === 'contained' ? withOpacity(baseColor, skin) : undefined,
    color: variant === 'contained' ? 'var(--color-white)' : baseColor,
    borderColor: variant === 'outlined' ? baseColor : 'transparent',
    opacity: isDisabled ? 0.6 : 1,
  }

  return (
    <Component
      className={cn(
        'inline-flex cursor-pointer items-center gap-2 px-4 py-2 transition-all duration-200 hover:opacity-90 hover:brightness-90',
        radiusMap[radius],
        justifyClasses,
        variantClasses,
        fullWidth && 'w-full',
        isDisabled ? 'cursor-not-allowed' : 'hover:opacity-90',
        className,
      )}
      style={dynamicStyles}
      disabled={Component === 'button' ? isDisabled : undefined}
      {...rest}
    >
      {loading ? (
        <svg
          className='h-4 w-4 animate-spin text-inherit'
          viewBox='0 0 24 24'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <circle
            className='opacity-25'
            cx='12'
            cy='12'
            r='10'
            stroke='currentColor'
            strokeWidth='4'
          />
          <path
            className='opacity-75'
            fill='currentColor'
            d='M12 2a10 10 0 0110 10h-4a6 6 0 00-6-6V2z'
          />
        </svg>
      ) : (
        <>
          {startContent}
          {children}
        </>
      )}
    </Component>
  )
}

const skinMap: Record<Skin, number> = {
  solid: 100,
  light: 75,
  'secondary-light': 30,
  ghost: 15,
}

function withOpacity(color: string, skin: Skin) {
  const opacity = skinMap[skin]
  return `color-mix(in oklab, ${color} ${opacity}%, transparent)
`
}
